// `GET /v1/devices` wrapper shared between extension and web clients.
// The endpoint and response shape are identical across surfaces; the only
// thing that varies is `apiBase` (and storage handling, which lives in the
// caller). Hosting this once in client-core means a future auth-scheme or
// endpoint-shape change is a single edit instead of two.
//
// The response is generic so callers can supply the richer `ListDevicesResponse`
// type from `@reflecto/core` without this package depending on it (keeps the
// crypto/wire-format split between `@reflecto/core` and `@reflecto/client-core`
// intact).

export interface DeviceListEntry {
  deviceId: string;
  /** User-set or auto-suggested display name. `null` until set. */
  name: string | null;
}

export interface DeviceListResult<T extends DeviceListEntry = DeviceListEntry> {
  devices: T[];
}

export async function listDevices<T extends DeviceListEntry = DeviceListEntry>(
  apiBase: string,
  token: string
): Promise<DeviceListResult<T>> {
  const res = await fetch(`${apiBase}/devices`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `GET /devices failed (${res.status})`);
  }
  const data = await res.json();
  return data as DeviceListResult<T>;
}
